import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ActivityType, ActivityTypeKind, CreateActivityTypeRequest, ScheduleType, UpdateActivityTypeRequest } from './activity-type.model';
import { environment } from '../../../environments/environment';

/**
 * Service for managing activity types.
 * Provides methods to list, create, update, and delete activity types.
 *
 * This service interacts with the backend API to perform CRUD operations on activity types.
 * The API endpoints are defined in the environment configuration.
 *
 * @author sadatmalik
 */
@Injectable({ providedIn: 'root' })
export class ActivityTypeService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiBaseUrl}/api/activity-types`;

  list(): Observable<ActivityType[]> {
    return this.http.get<ActivityType[]>(this.baseUrl);
  }

  create(request: CreateActivityTypeRequest): Observable<ActivityType> {
    return this.http.post<ActivityType>(this.baseUrl, request);
  }

  update(id: string, request: UpdateActivityTypeRequest): Observable<ActivityType> {
    return this.http.put<ActivityType>(`${this.baseUrl}/${id}`, request);
  }

  reorder(
    orderedIds: string[],
    options: { kind: ActivityTypeKind; scheduleType?: ScheduleType }
  ): Observable<ActivityType[]> {
    return this.http.put<ActivityType[]>(`${this.baseUrl}/order`, {
      orderedIds,
      kind: options.kind,
      ...(options.scheduleType ? { scheduleType: options.scheduleType } : {})
    });
  }

  moveToScheduleType(id: string, scheduleType: ScheduleType): Observable<ActivityType> {
    return this.http.patch<ActivityType>(`${this.baseUrl}/${id}/schedule-type`, { scheduleType });
  }

  delete(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${id}`);
  }

  markNotThisCycle(id: string): Observable<ActivityType> {
    return this.http.put<ActivityType>(`${this.baseUrl}/${id}/not-this-cycle`, {});
  }

  clearNotThisCycle(id: string): Observable<ActivityType> {
    return this.http.delete<ActivityType>(`${this.baseUrl}/${id}/not-this-cycle`);
  }
}
